import React, { useEffect, useState } from 'react'
import { Form, Input, Typography, Space, Select, Row, Col } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { v4 as uuidv4 } from 'uuid'
import { register } from '../../../api/modules/api-user'
import { useStores } from '../../../cls/stores/MobXProvider'
import { startError } from '../../../stores/work-progress/actions'
import { GeneralContentWrapper } from '../../../components/Shared/GeneralComponents'
import { countriesOptions } from '../../../config/utils'
import { getGMTOffset } from '../../../config'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import { actSaveUser } from '../../../stores/users/actions'
import { SubmitButton } from '../../../components/Form'
import { toJS } from 'mobx'
import PropTypes from 'prop-types'
const { Title, Text } = Typography // Import Typography for text styling

/**
 * UserRegister
 *
 * A React component for rendering the user registration form.
 * Handles the creation of a new user account, auto-login on success,
 * and redirection to the dashboard. Includes country, timezone, and email/password fields.
 *
 * @component
 * @returns {JSX.Element} The rendered UserRegister form.
 */
const UserRegister = ({ actionOnLogin, hideRelated }) => {
	const { t } = useSmartTranslation() // Translation function
	const [loadingItm, setLoadingItm] = useState(false)

	const { users, appType } = useStores()
	const { appIsClient, appIsTeam } = appType

	const [form] = Form.useForm()
	const navigate = useNavigate()

	const { isLoggedIn, loggedInUserId: curentUser } = users
	useEffect(() => {
		if (isLoggedIn) {
			if (typeof actionOnLogin === 'function') {
				actionOnLogin()
				return
			}
			// Redirect to dashboard if user is already logged in
			navigate('/dashboard')
		}
	}, [curentUser, navigate])

	/**
	 * onFinish
	 *
	 * Handles the registration form submission. Validates that the passwords match,
	 * sends the data to the backend via the `register` API call,
	 * and sets the user as logged in if registration is successful.
	 *
	 * @param {Object} values - The form values, including email, password, and optional metadata.
	 * @returns {Promise<void>}
	 */
	const onFinish = async (values) => {
		// console.log('Register payload:', values)
		const newId = uuidv4()
		values.user_id = newId
		if (appIsClient) {
			values.user_type = 'client'
		} else if (appIsTeam) {
			values.user_type = 'team'
		}

		// check password match
		if (values.password !== values.password_match) {
			startError(t('single.password.not-match'))
			return
		}
		console.log('Register payload:', values)

		try {
			setLoadingItm(true)
			register(values)
				.then((response) => {
					if (response.status === 'success') {
						const userId = response.user.user_id
						users.setLoggedInUser(userId, response.apiToken)
						const user = users.requireUserById(userId)
						user.updateFromServer(response.user)
						user.setName(values.email)
						user.setEmail(values.email)
						user.setDiscount(values.discount)

						actSaveUser(
							user.user_id,
							toJS(user.full),
							toJS(user.allowed)
						).then(() => {
							users.saveState()
							setLoadingItm(false)
							if (typeof actionOnLogin === 'function') {
								actionOnLogin()
								return
							}
							navigate('/dashboard')
						})
					} else {
						startError(response.message)
					}
				})
				.catch((error) => {
					console.error('Failed to fetch user info:', error)
				})
		} catch (error) {
			alert(t('single.signup.fail'))
			console.error('Registration error:', error)
		}
	}

	return (
		<GeneralContentWrapper>
			<Space
				direction="vertical"
				style={{ width: '100%', marginBottom: '20px' }}
			>
				<Title level={2}>{t('single.signup.now')}</Title>
				<p>{t('single.fill-details-to-start')}</p>
			</Space>

			<Form
				form={form}
				name="register"
				onFinish={onFinish}
				layout="vertical"
				initialValues={{
					// Set default values for the form fields
					country: '',
					timezone: getGMTOffset(),
					email: '',
					password: '',
					password_match: '',
					discount: ''
				}}
			>
				<Row gutter={16}>
					<Col xs={24} sm={12}>
						{/* Country (new field) */}
						<Form.Item
							name="country"
							label={t('single.country')}
							rules={[
								{
									required: true,
									message: t('__select.please')
								}
							]}
						>
							<Select
								showSearch
								allowClear
								placeholder={t('single.country')}
								options={countriesOptions}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item
							label={t('single.time-zone')}
							name={'timezone'}
						>
							<Input placeholder={getGMTOffset()} />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<Form.Item
							name="email"
							label={t('single.email')}
							rules={[
								{
									required: true,
									message: t('__provide.please%single.email'),
									type: 'email'
								}
							]}
						>
							<Input placeholder={t('single.email')} />
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item name="discount" label={t('single.discount')}>
							<Input placeholder={t('single.discount')} />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24} sm={12}>
						<Form.Item
							name="password"
							label={t('single.password.label')}
							rules={[
								{
									required: true,
									message: t(
										'__provide.please%single.password.label'
									)
								}
							]}
						>
							<Input.Password
								placeholder={t('single.password.label')}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12}>
						<Form.Item
							name="password_match"
							label={t('single.password.match')}
							rules={[
								{
									required: true,
									message: t(
										'__provide.please%single.password.label'
									),
									validator: (_, value) => {
										if (
											!value ||
											form.getFieldValue('password') ===
												value
										) {
											return Promise.resolve()
										}
										return Promise.reject(
											new Error(
												t('single.password.not-match')
											)
										)
									}
								}
							]}
						>
							<Input.Password
								placeholder={t('single.password.label')}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Space
					style={{ width: '100%' }}
					direction="horizontal"
					align="center"
					justify="center"
				>
					<SubmitButton loading={loadingItm} style={{ marginTop: 0 }}>
						{t('single.signup.label')}
					</SubmitButton>

					{!hideRelated && (
						<Text style={{ display: 'block', textAlign: 'center' }}>
							{t('single.login.existing-account')}{' '}
							<Link to="../login">{t('single.login.now')}</Link>
						</Text>
					)}
				</Space>
			</Form>
		</GeneralContentWrapper>
	)
}
UserRegister.propTypes = {
	actionOnLogin: PropTypes.func,
	hideRelated: PropTypes.bool
}

export default observer(UserRegister)
